import { Injectable } from '@angular/core';
import { isVoid } from '../../utils';
import { UserSettingsService } from '../../user-settings.service';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { SessionService } from '../../authentication/session-service.service';
import { RecordCollarsTransaction } from '../../shell-communication/collars-operations';
import {
   CollarsAfterState,
   CollarsAfterStateLeg,
   CollarsActualFillInput,
   COLLARS_AFTER_STATE_PREFIX,
   collarsAfterStateKey
} from './collars-after-state.models';

/**
 * Persistence + apply for Collars After State. Storage is user settings (one key per leaf group:
 * `collars.after-state.<groupBucketId>`), the SAME pattern every panel uses — NO database, NO new backend
 * op for storage. The indicator is derived from the existence of these keys.
 *
 * Settings read/write/enumerate go through the verified UserSettingsService API; apply books one
 * RecordCollarsTransaction per derived leg (close-before-open by legOrder) then deletes the consumed keys.
 */
@Injectable()
export class CollarsAfterStateService {

   constructor(
      private readonly _userSettings: UserSettingsService,
      private readonly _shellClient: ShellClientService,
      private readonly _session: SessionService
   ) { }

   /**
    * Persist a leaf group's end-state legs. One key per group. Returns the persistence Promise so callers
    * can await server-side completion before closing the dialog (avoids the in-flight loss noted in design).
    */
   saveAfterState(groupBucketId: string, terminalId: string, legs: CollarsAfterStateLeg[]): Promise<void> {
      const value: CollarsAfterState = {
         groupBucketId,
         terminalId,
         legs: legs || [],
         savedAtIso: new Date().toISOString()
      };
      return this._userSettings.setValue(collarsAfterStateKey(groupBucketId), value);
   }

   /** Read a leaf group's saved end-state, or null when none is stored. */
   getAfterState(groupBucketId: string): CollarsAfterState | null {
      const value = this._userSettings.getValue<CollarsAfterState>(collarsAfterStateKey(groupBucketId));
      if (!value) { return null; }
      // Legacy-tolerant load, NO stored-data migration:
      //  - One envelope covers exactly ONE leaf group, so legs persisted without a groupBucketId (older
      //    saves) inherit the envelope's — keeps preview/apply bucketing group-aware.
      //  - Plans saved before the expiration normalization may hold the picker's OptionExpirationDescriptor
      //    OBJECT in `expiration`; collapse it to its date string so the date pipe can format it.
      (value.legs || []).forEach(leg => {
         if (!leg.groupBucketId) { leg.groupBucketId = value.groupBucketId; }
         const exp: any = leg.expiration;
         if (exp && typeof exp === 'object' && exp.optionExpirationDate) {
            leg.expiration = exp.optionExpirationDate;
         }
      });
      return value;
   }

   /**
    * Enumerate the leaf-group ids that currently have an after-state PLAN (drives the green indicator).
    * Uses a plain String.startsWith predicate (no regex) on the current user's own settings.
    *
    * KEY PRESENCE IS NOT PLAN EXISTENCE: a deleted setting can re-enter the settings cache as a key with a
    * NULL value — UserSettingsService.processUserSettings (app bootstrap) writes
    * `JSON.parse(ust.value || null)` under every key the backend returns, and getItemsMatchingPattern
    * enumerates keys regardless of value. Without the value filter, an applied/discarded plan's tombstone
    * resurrected the green badge on every app reload.
    */
   listAfterStateGroupIds(): string[] {
      const userId = this._session.sessionData ? this._session.sessionData.userId : null;
      if (!userId) { return []; }
      const items = this._userSettings.getItemsMatchingPattern<CollarsAfterState>(
         (key: string) => key.indexOf(COLLARS_AFTER_STATE_PREFIX) === 0,
         userId
      );
      return items
         .filter(i => !isVoid(i.value))
         .map(i => i.key.substring(COLLARS_AFTER_STATE_PREFIX.length));
   }

   /** Remove a leaf group's after-state key (called after a successful Apply, or to abandon a plan). */
   discard(groupBucketId: string): Promise<void> {
      // deleteValue is fire-and-forget (no Promise) — wrap so callers can uniformly await.
      this._userSettings.deleteValue(collarsAfterStateKey(groupBucketId));
      return Promise.resolve();
   }

   /**
    * Apply the saved end-state for a scope: book the derived transaction with the operator's ACTUAL fills,
    * then delete the touched after-state key(s).
    *
    * Booking order matters: fills are sorted by ascending `legOrder` so a side-reverse books Close-first then
    * Open (the diff emits the close with the lower legOrder). Each fill becomes ONE
    * `RecordCollarsTransaction` routed to its own leaf group (`fill.groupBucketId`) — so a Portfolio/Combo
    * scope that touched several leaf groups books each leg into the right group. The owning terminal for a
    * leg is read from that group's SAVED after-state (`getAfterState(groupBucketId).terminalId`), which is the
    * terminal that authored the plan; the session user only owns the settings keys, not necessarily the seat.
    * Legs are booked sequentially (await one before the next) to preserve the close-before-open ordering.
    * Only after EVERY leg books do we delete the consumed after-state key(s) — a mid-way failure leaves the
    * plan intact so the operator can retry.
    */
   async applyAfterState(scopeBucketId: string, fills: CollarsActualFillInput[]): Promise<void> {
      const ordered = (fills || []).slice().sort((a, b) => a.legOrder - b.legOrder);

      if (ordered.length === 0) {
         return;
      }

      // Distinct leaf groups consumed by this apply (deleted only after a fully successful booking pass).
      const touchedGroupIds: string[] = [];

      for (let i = 0; i < ordered.length; i++) {
         const fill = ordered[i];
         const saved = this.getAfterState(fill.groupBucketId);
         const terminalId = saved ? saved.terminalId : null;

         const cmd = new RecordCollarsTransaction(
            terminalId,
            fill.groupBucketId,
            fill.kind,
            fill.tradingInstrumentCode,
            fill.qty,
            fill.side,
            fill.actualAvgPx
         );

         // Sequential await keeps close-before-open ordering; a rejection aborts before any key is deleted.
         await this._shellClient.processCommand(cmd);

         if (touchedGroupIds.indexOf(fill.groupBucketId) < 0) {
            touchedGroupIds.push(fill.groupBucketId);
         }
      }

      // All legs booked — retire the consumed plans so the green indicator clears for these groups.
      for (let j = 0; j < touchedGroupIds.length; j++) {
         await this.discard(touchedGroupIds[j]);
      }
   }
}
